import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { FleetProvider } from './context/FleetContext';
import { LoyaltyProvider } from './context/LoyaltyContext';
import { NotificationProvider } from './components/common/NotificationSystem';
import ProtectedRoute from './components/common/ProtectedRoute';
import PreviewGate from './components/common/PreviewGate';
import ErrorBoundary from './components/common/ErrorBoundary';
import RouteLoading from './components/common/RouteLoading';
import { grantPreviewAccessFromURL } from './utils/previewAccess';

// Avant l'ouverture au public, /login, /register et les dashboards sont
// verrouillés (PreviewGate) — un lien secret ?acces=CODE débloque l'accès
// sur l'appareil qui l'ouvre, sans rien exposer publiquement.
grantPreviewAccessFromURL();

// Chaque page est chargée à la demande (code-splitting par route) plutôt que
// toutes regroupées dans un seul gros fichier JS — un client, un chauffeur ou
// un admin ne télécharge que le code de son propre espace. Utile pour des
// utilisateurs sur réseau mobile à Dakar.

// Pages publiques
const IntroPage = lazy(() => import('./pages/IntroPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const CorporateRequest = lazy(() => import('./pages/CorporateRequest'));
const PublicTrackRide = lazy(() => import('./pages/PublicTrackRide'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const LoggedOut = lazy(() => import('./pages/LoggedOut'));

// Pages Client
const ClientLayout = lazy(() => import('./pages/client/ClientLayout'));
const ClientDashboard = lazy(() => import('./pages/client/ClientDashboard'));
const BookRide = lazy(() => import('./pages/client/BookRide'));
const EventBooking = lazy(() => import('./pages/client/EventBooking'));
const IntercityBooking = lazy(() => import('./pages/client/IntercityBooking'));
const AirportTransfer = lazy(() => import('./pages/client/AirportTransfer'));
const TrackRide = lazy(() => import('./pages/client/TrackRide'));
const RideHistory = lazy(() => import('./pages/client/RideHistory'));
const ClientProfile = lazy(() => import('./pages/client/ClientProfile'));
const Subscription = lazy(() => import('./pages/client/Subscription'));

// Pages Chauffeur
const DriverLayout = lazy(() => import('./pages/driver/DriverLayout'));
const DriverDashboard = lazy(() => import('./pages/driver/DriverDashboard'));
const DriverRides = lazy(() => import('./pages/driver/DriverRides'));
const DriverRideDetail = lazy(() => import('./pages/driver/DriverRideDetail'));
const DriverEarnings = lazy(() => import('./pages/driver/DriverEarnings'));
const DriverProfile = lazy(() => import('./pages/driver/DriverProfile'));
const DriverNotifications = lazy(() => import('./pages/driver/DriverNotifications'));

// Pages Admin
// ManageUsers/ManageRides/SecurityMonitor/Analytics.jsx supprimées : pages en
// doublon des vrais onglets internes de AdminDashboard.jsx (Utilisateurs,
// Courses, Sécurité, Analytics), jamais reliées à aucun menu, entièrement
// codées en dur, et accessibles par URL directe avec des données qui
// contredisaient les vraies (voir l'audit du 2026-08-13).
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));

function App() {
  return (
    <Router>
      <AuthProvider>
        <SocketProvider>
          <FleetProvider>
          <LoyaltyProvider>
          <NotificationProvider>
            <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: '#1e293b',
                color: '#f1f1f1',
                border: '1px solid #1e3a5f',
                fontFamily: "'DM Sans', sans-serif",
              },
              success: { iconTheme: { primary: '#3b82f6', secondary: '#fff' } },
            }}
          />
          <ErrorBoundary>
          <Suspense fallback={<RouteLoading />}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<IntroPage />} />
            <Route path="/decouvrir" element={<LandingPage />} />
            <Route path="/corporate" element={<CorporateRequest />} />
            {/* Suivi de trajet partagé — public, sans compte (voir server/rides.js
                GET /rides/track/:id). Hors PreviewGate/ProtectedRoute exprès : un
                proche qui reçoit ce lien n'a ni compte ni accès anticipé. */}
            <Route path="/suivi/:rideId" element={<PublicTrackRide />} />

            {/* Connexions et dashboards — verrouillés tant que la plateforme
                n'est pas ouverte au public (voir PreviewGate) */}
            <Route element={<PreviewGate />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/logged-out" element={<LoggedOut />} />

              {/* Client routes */}
              <Route path="/client" element={<ProtectedRoute role="client" />}>
                <Route element={<ClientLayout />}>
                  <Route index element={<ClientDashboard />} />
                  <Route path="book" element={<BookRide />} />
                  <Route path="intercity" element={<IntercityBooking />} />
                  <Route path="aeroport" element={<AirportTransfer />} />
                  <Route path="event/:eventKey" element={<EventBooking />} />
                  <Route path="track/:rideId" element={<TrackRide />} />
                  <Route path="history" element={<RideHistory />} />
                  <Route path="profile" element={<ClientProfile />} />
                  <Route path="subscription" element={<Subscription />} />
                </Route>
              </Route>

              {/* Driver routes */}
              <Route path="/driver" element={<ProtectedRoute role="driver" />}>
                <Route element={<DriverLayout />}>
                  <Route index element={<DriverDashboard />} />
                  <Route path="rides" element={<DriverRides />} />
                  <Route path="rides/:rideId" element={<DriverRideDetail />} />
                  <Route path="earnings" element={<DriverEarnings />} />
                  <Route path="profile" element={<DriverProfile />} />
                  <Route path="notifications" element={<DriverNotifications />} />
                </Route>
              </Route>

              {/* Admin routes */}
              <Route path="/admin" element={<ProtectedRoute role="admin" />}>
                <Route index element={<AdminDashboard />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/decouvrir" replace />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
          </NotificationProvider>
          </LoyaltyProvider>
          </FleetProvider>
        </SocketProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
